import type { Axis, MoveMessage } from './net/protocol';

/**
 * Touch controls: an on-screen pad for walking (phones and tablets have no W key), and
 * the maths for pinch-to-zoom. The pad gives the same control state as the keyboard (forward,
 * turn, run), so the gateway and the grid see no difference.
 */

export type Controls = Omit<MoveMessage, 'type'>;

/** The knob must move this share of the pad's radius before it counts: thumbs wobble. */
const DEAD_ZONE = 0.3;
/** Pushed this far forward (share of the radius), walking becomes running. */
const RUN_FROM = 0.85;

/**
 * Pad knob offset (dx right, dy down, in pixels) to controls, for a pad of `radius` pixels.
 * Up walks forward, down backs up, left turns left; each axis has a dead zone, and pushing
 * all the way forward runs. The protocol's controls are on/off (-1, 0, 1), like keys.
 */
export function stickControls(dx: number, dy: number, radius: number): Controls {
  const r = Math.max(1, radius);
  const x = dx / r;
  const y = -dy / r; // screen y grows downwards; forward is up
  const axis = (v: number): Axis => (v > DEAD_ZONE ? 1 : v < -DEAD_ZONE ? -1 : 0);
  const forward = axis(y);
  // Left turn is positive (counter-clockwise, Z-up), as in controlsFromKeys.
  const turn = axis(-x);
  return { forward, turn, run: forward === 1 && y > RUN_FROM };
}

/** How far the knob is drawn from the pad's centre: the drag, held inside the pad. */
export function knobOffset(dx: number, dy: number, radius: number): { x: number; y: number } {
  const length = Math.hypot(dx, dy);
  const scale = length > radius ? radius / length : 1;
  return { x: dx * scale, y: dy * scale };
}

/**
 * Pinch: the zoom ratio for two fingers moving from `before` to `after` apart (pixels).
 * Spreading them (ratio > 1) brings the camera closer, as on a map. Tiny or invalid
 * distances give 1 (no zoom), so a finger landing on top of another can't fling the camera.
 */
export function pinchRatio(before: number, after: number): number {
  if (!(before > 8) || !(after > 8) || !Number.isFinite(before) || !Number.isFinite(after)) return 1;
  return after / before;
}

/**
 * The walking pad: a round base with a knob you drag. Reports controls on every change and
 * zero when the finger lifts (or the browser cancels the touch, e.g. a notification).
 */
export class TouchStick {
  private pointer: number | null = null;
  private origin = { x: 0, y: 0 };
  private last = '';

  constructor(
    private readonly pad: HTMLElement,
    private readonly knob: HTMLElement,
    private readonly onControls: (controls: Controls) => void,
  ) {
    pad.addEventListener('pointerdown', this.onDown);
    pad.addEventListener('pointermove', this.onMove);
    pad.addEventListener('pointerup', this.onUp);
    pad.addEventListener('pointercancel', this.onUp);
    pad.addEventListener('lostpointercapture', this.onUp);
  }

  private radius(): number {
    return this.pad.clientWidth / 2;
  }

  private readonly onDown = (e: PointerEvent): void => {
    if (this.pointer !== null) return;
    this.pointer = e.pointerId;
    // The pad's centre, not where the thumb landed: pressing above the centre walks at once.
    const box = this.pad.getBoundingClientRect();
    this.origin = { x: box.left + box.width / 2, y: box.top + box.height / 2 };
    try {
      this.pad.setPointerCapture(e.pointerId);
    } catch {
      /* no capture: the drag still works while the thumb stays on the pad */
    }
    e.preventDefault();
    this.update(e);
  };

  private readonly onMove = (e: PointerEvent): void => {
    if (e.pointerId === this.pointer) this.update(e);
  };

  private readonly onUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointer) return;
    this.pointer = null;
    this.knob.style.transform = '';
    this.emit({ forward: 0, turn: 0, run: false });
  };

  private update(e: PointerEvent): void {
    const dx = e.clientX - this.origin.x;
    const dy = e.clientY - this.origin.y;
    const r = this.radius();
    const k = knobOffset(dx, dy, r);
    this.knob.style.transform = `translate(${k.x}px, ${k.y}px)`;
    this.emit(stickControls(dx, dy, r));
  }

  private emit(controls: Controls): void {
    const key = `${controls.forward}|${controls.turn}|${controls.run}`;
    if (key === this.last) return;
    this.last = key;
    this.onControls(controls);
  }
}
